import { useCallback, useRef, useState } from 'react'
import { concat, isEmpty } from '@/utils/lodash'
import { parseEmails, uniqueEmails } from '@/utils/shareEmails'

export function useShareEmails(initialEmails = []) {
  const [emails, setEmails] = useState(initialEmails)
  const [emailInput, setEmailInput] = useState('')
  const emailsRef = useRef(initialEmails)
  const emailInputRef = useRef('')

  const updateEmails = useCallback((next) => {
    emailsRef.current = next
    setEmails(next)
  }, [])

  const updateEmailInput = useCallback((next) => {
    emailInputRef.current = next
    setEmailInput(next)
  }, [])

  const resetEmails = useCallback((next = []) => {
    emailsRef.current = next
    emailInputRef.current = ''
    setEmails(next)
    setEmailInput('')
  }, [])

  const collectEmails = useCallback(() => {
    const finalEmails = uniqueEmails(
      concat(emailsRef.current, parseEmails(emailInputRef.current)),
    )
    updateEmails(finalEmails)
    updateEmailInput('')
    return finalEmails
  }, [updateEmails, updateEmailInput])

  const onInputChange = useCallback(
    (event, value, reason) => {
      if (reason === 'reset') return
      updateEmailInput(value)
    },
    [updateEmailInput],
  )

  const onChange = useCallback(
    (event, inputs, reason) => {
      if (reason === 'createOption' || reason === 'blur') {
        const validatedItems = parseEmails(inputs[inputs.length - 1])
        if (isEmpty(validatedItems)) return
        updateEmails(
          uniqueEmails(concat(emailsRef.current, validatedItems)),
        )
        updateEmailInput('')
      } else if (reason === 'removeOption') {
        updateEmails(inputs)
      } else if (reason === 'clear') {
        updateEmails([])
        updateEmailInput('')
      }
    },
    [updateEmails, updateEmailInput],
  )

  const preventEnterSubmit = useCallback(
    (params) => (event) => {
      params.inputProps?.onKeyDown?.(event)
      if (event.key === 'Enter') {
        event.preventDefault()
      }
    },
    [],
  )

  return {
    emails,
    emailInput,
    resetEmails,
    collectEmails,
    onInputChange,
    onChange,
    preventEnterSubmit,
  }
}
